"use client";
import useClickOutside from "@/hooks/use-click-outside";
import { Lang } from "@/i18n/settings";
import { cn } from "@/lib/utils";
import { AnimatePresence, motion, Variants } from "framer-motion";
import { useRef, useId } from "react";
import CloseBtTn from "./close-button";
import Divider from "./divider";
import Trophy from "./trophy";

type CardProps = {
  title: string;
  description: string;
  isExpanded: boolean;
  onClick: () => void;
  onClose: () => void;
  index: number;
  isLayoutExpanded: boolean;
  cardWidth?: string;
  lang: Lang;
  hoveredIndex: number | null;
  onHover: () => void;
  onHoverEnd: () => void;
};

export function ExpandableCard({
  title,
  description,
  isExpanded,
  onClick,
  onClose,
  index,
  isLayoutExpanded,
  lang,
  hoveredIndex,
  onHover,
  onHoverEnd,
}: CardProps) {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const cardGradientId = useId();

  useClickOutside(cardRef, () => onClose);

  const cardVariants: Variants = {
    initial: {
      position: "relative",
      zIndex: 1,
      // width: cardWidth,
    },
    expanded: {
      position: "absolute",
      top: 0,
      width: "700px",
      zIndex: 10,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10,
        ease: "easeInOut",
      },
    },
  };

  const descriptionVariants: Variants = {
    hidden: {
      opacity: 0,
      x: -10,
    },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10,
        delay: 0.3,
      },
    },
  };

  const maxWidth = 700;

  const leftOffset = index > 2 ? `calc(100% - ${maxWidth}px)` : 0;

  return (
    <div ref={cardRef} className="relative">
      <motion.div
        onHoverStart={() => onHover?.()}
        onHoverEnd={() => onHoverEnd?.()}
        onClick={onClick}
        variants={cardVariants}
        initial="initial"
        animate={isExpanded ? "expanded" : "initial"}
        className={cn(
          "relative cursor-pointer overflow-hidden rounded-[20px] transition-all duration-150 ease-linear",
          isExpanded
            ? "z-50 bg-[#3A285C33] backdrop-blur-md"
            : "bg-white/10 backdrop-blur-md hover:scale-105"
        )}
        layout
        style={{
          right: lang === "ar" && isExpanded ? leftOffset : 0,
          left: lang === "en" && isExpanded ? leftOffset : 0,
          maxWidth: isExpanded ? `${maxWidth}px` : "300px",
        }}
      >
        <svg
          className="pointer-events-none absolute inset-0 z-0 h-full w-full"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
        >
          <defs>
            <linearGradient
              id={cardGradientId}
              x1="840"
              y1="0"
              x2="840"
              y2="436"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#F0DFC1" />
              <stop offset="0.791452" stopColor="#C89452" />
              <stop offset="1" stopColor="#B07F44" />
            </linearGradient>
          </defs>
          <rect
            x="0.5"
            y="1"
            width="calc(100% - 1px)"
            height="calc(100% - 1px)"
            rx="20"
            ry="20"
            fill="none"
            stroke={`url(#${cardGradientId})`}
            strokeWidth="1"
          />
        </svg>

        <div
          className={cn(
            "relative flex items-center px-6 py-12",
            isExpanded ? "text-secondary" : "text-primary",
            isLayoutExpanded ? "text-secondary" : "text-primary",
            isLayoutExpanded && !isExpanded ? "opacity-30" : ""
          )}
        >
          <div className="flex items-center gap-6">
            <span className="flex-shrink-0">
              <Trophy
              // isExpanded={
              //   isExpanded ||
              //   (hoveredIndex !== null && hoveredIndex === index)
              // }
              />
            </span>
            <p
              className={cn(
                "w-[120px] text-[#F3E4CE] 3xl:text-xl ltr:font-playfair",
                hoveredIndex !== null && hoveredIndex === index ? "text-secondary" : ""
              )}
            >
              {title}
            </p>

            <AnimatePresence mode="wait" initial={false}>
              {isExpanded && (
                <motion.div
                  variants={descriptionVariants}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  className="flex w-[400px] items-center gap-8"
                >
                  <Divider />
                  {description}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {isExpanded && (
          <motion.button
            className="absolute top-2 rounded-full bg-white p-2 shadow ltr:right-2 rtl:left-2"
            onClick={(e) => {
              e.stopPropagation();

              onClose();
            }}
          >
            <CloseBtTn />
          </motion.button>
        )}
      </motion.div>
    </div>
  );
}
